import { createAnalyticsHeaders } from "./analytics-attribution";
import Cookies from "js-cookie";
import configs from "./configs";
import { getAuthHeaders, handleInstantAccessUnauthorized } from "./getToken";
import { AUTH_MODE_INSTANT, resolveAuthMode } from "./reauth-hints";

export function getCookieToken() {
  const isInstantAccessMode = resolveAuthMode(localStorage) === AUTH_MODE_INSTANT;
  const instantToken = isInstantAccessMode ? localStorage.getItem("auth0token") : null;
  return instantToken || Cookies.get(configs["JWT_COOKIE"]);
}

const analyticsHeaders = createAnalyticsHeaders(
  () => (typeof window === "undefined" ? undefined : window),
  () => configs
);

export async function apiRequest(method, url, body) {
  let authHeaders = await getAuthHeaders(url, method);
  if (!authHeaders.Authorization) {
    const cookie = getCookieToken();
    authHeaders = cookie ? { Authorization: `Bearer ${cookie}` } : {};
  }

  const response = await fetch(url, {
    method: method,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...authHeaders,
      ...(await analyticsHeaders(url, method))
    },
    body: JSON.stringify(body)
  }).catch(e => {
    console.error(e);
    return false;
  });

  if (!response.ok) {
    handleInstantAccessUnauthorized(response.status);
    console.error(response);
    return false;
  }

  const data = await response.json();

  return data;
}

export function getEventsCookie() {
  return JSON.parse(Cookies.get("events") || "[]");
}

export async function userIsInTheEvent(_user, slug) {
  const url = configs["CUSTOM_API_URL"] + "/events/" + encodeURIComponent(slug);
  const event = await apiRequest("GET", url);
  return event?.current_user?.registration?.status === "confirmed";
}

export function getStartTime(data) {
  return new Date(data?.start_time_next ?? data?.start_time ?? data?.starts_at).getTime();
}

export function getEndTime(data) {
  if (!data) return 0;

  return new Date(data.end_time_next ?? data.end_time ?? data.ends_at).getTime();
}

function text(value) {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function scheduleTimeZone(timeZone) {
  return text(timeZone) || undefined;
}

export function eventScheduleDayKey(value, timeZone) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const zone = scheduleTimeZone(timeZone);
  const parts = new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    ...(zone ? { timeZone: zone } : {})
  }).formatToParts(date);
  const get = type => parts.find(part => part.type === type)?.value || "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

export function formatEventScheduleDay(value, timeZone) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const zone = scheduleTimeZone(timeZone);
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    ...(zone ? { timeZone: zone } : {})
  }).format(date);
}

export function formatEventScheduleTime(value, timeZone) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const zone = scheduleTimeZone(timeZone);
  return new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
    ...(zone ? { timeZone: zone } : {})
  }).format(date);
}

export function eventRoomContext(userData) {
  const event = userData?.event;
  const roomRole = event?.roomRole === "session" ? "session" : "lobby";
  return {
    eventId: text(event?.eventId),
    eventSlug: text(event?.eventSlug) || text(userData?.event_slug),
    roomRole,
    sessionSlug: roomRole === "session" ? text(event?.sessionSlug) : null,
    visibility: ["private", "unlisted", "public"].includes(event?.visibility) ? event.visibility : null
  };
}

export function getEventSession(event, sessionSlug) {
  if (!sessionSlug) return null;
  return event?.presentations?.find(presentation => presentation.slug === sessionSlug) || null;
}

function uiAttendanceMode(mode) {
  return mode === "physical" ? "in_person" : mode || "virtual";
}

function hasConfirmedRegistration(currentUser) {
  return currentUser?.registration?.status === "confirmed";
}

function bookedSessionIds(currentUser) {
  return new Set(
    (currentUser?.bookings || []).filter(booking => booking?.status === "confirmed").map(booking => booking.session_id)
  );
}

function sessionToPresentation(session, bookedIds) {
  return {
    id: session.id,
    slug: session.slug,
    name: session.name,
    title: session.name,
    ...(session.description !== undefined ? { description: session.description } : {}),
    type: uiAttendanceMode(session.attendance_mode),
    start_time: session.starts_at,
    end_time: session.ends_at,
    ...(session.timezone !== undefined ? { timezone: session.timezone } : {}),
    capacity: session.capacity,
    attendees_count: session.attendees_count,
    is_sold_out: session.is_sold_out,
    entry_available: session.entry_available,
    entry_kind: session.entry_kind || null,
    is_attending: bookedIds.has(session.id),
    ...(session.address !== undefined ? { address: session.address } : {}),
    ...(session.google_calendar_url !== undefined ? { google_calendar_url: session.google_calendar_url } : {}),
    ...(session.outlook_calendar_url !== undefined ? { outlook_calendar_url: session.outlook_calendar_url } : {}),
    presenters: (session.presenters || []).map(presenter => ({
      name: presenter.name,
      image: presenter.image_url || presenter.external_image_url || null
    }))
  };
}

// The room UI predates EventDetail. Keep its display shape local, but never add
// Room bindings, entry URLs, or another caller's participation data to it.
export function eventDetailToUiModel(eventDetail) {
  if (!eventDetail) return null;

  const currentUser = eventDetail.current_user || null;
  const bookedIds = bookedSessionIds(currentUser);
  return {
    slug: eventDetail.slug,
    name: eventDetail.name,
    ...(eventDetail.description !== undefined ? { description: eventDetail.description } : {}),
    start_time: eventDetail.starts_at,
    end_time: eventDetail.ends_at,
    ...(eventDetail.timezone !== undefined ? { timezone: eventDetail.timezone } : {}),
    type: uiAttendanceMode(eventDetail.attendance_mode),
    ...(eventDetail.visibility !== undefined ? { visibility: eventDetail.visibility } : {}),
    capacity: eventDetail.capacity,
    attendees_count: eventDetail.attendees_count,
    is_sold_out: eventDetail.is_sold_out,
    ...(eventDetail.entry_kind !== undefined ? { entry_kind: eventDetail.entry_kind } : {}),
    ...(eventDetail.address !== undefined ? { address: eventDetail.address } : {}),
    ...(eventDetail.google_calendar_url !== undefined ? { google_calendar_url: eventDetail.google_calendar_url } : {}),
    ...(eventDetail.outlook_calendar_url !== undefined
      ? { outlook_calendar_url: eventDetail.outlook_calendar_url }
      : {}),
    ...(eventDetail.ticket_offers !== undefined ? { ticket_offers: eventDetail.ticket_offers } : {}),
    current_user: currentUser,
    is_attending: hasConfirmedRegistration(currentUser),
    presentations: (eventDetail.sessions || []).map(session => sessionToPresentation(session, bookedIds))
  };
}

export async function getEvent(slug) {
  const url = configs["CUSTOM_API_URL"] + "/events/" + encodeURIComponent(slug);
  return eventDetailToUiModel(await apiRequest("GET", url));
}

export function admissionEntryForCurrentCaller(receipt) {
  if (!receipt?.admitted || !receipt.entry?.url) return null;
  return {
    url: receipt.entry.url
  };
}

export function isCurrentRoomEntry(entryUrl, currentUrl) {
  if (typeof entryUrl !== "string" || typeof currentUrl !== "string") return false;

  try {
    const entry = new URL(entryUrl, currentUrl);
    const current = new URL(currentUrl);
    const normalizePathname = pathname => pathname.replace(/\/+$/, "") || "/";

    return entry.origin === current.origin && normalizePathname(entry.pathname) === normalizePathname(current.pathname);
  } catch {
    return false;
  }
}

export function entryUrlFromSessionShare(share) {
  return text(share?.entry?.url) || text(share?.room?.short_url) || text(share?.room?.url);
}

export function enterEventRoom({ isEvent, hasConcluded, action, open, admit }) {
  if (!isEvent || hasConcluded) return open(action);
  return admit();
}

export function enterScheduledSession({ sessionSlug, hasConcluded, visitConcluded, admit }) {
  return hasConcluded ? visitConcluded(sessionSlug) : admit(sessionSlug);
}

export function admissionDenialMessage(reason) {
  const messages = {
    registration_required: "Register for this activation before joining.",
    registration_awaiting_payment: "Complete registration payment before joining.",
    registration_revoked: "Your activation registration is no longer active.",
    session_booking_required: "Reserve this presentation before joining.",
    session_booking_awaiting_payment: "Complete presentation payment before joining.",
    session_booking_revoked: "Your presentation reservation is no longer active.",
    session_entry_unavailable: "This presentation is not configured for entry.",
    event_not_started: "This activation has not started yet.",
    session_not_started: "This presentation has not started yet.",
    event_concluded: "This activation has concluded.",
    session_concluded: "This presentation has concluded.",
    room_capacity_full: "This verse is currently full. Please try again shortly."
  };
  return messages[reason] || "This activation is not available to join right now.";
}

export function eventHasStarted(startTime) {
  return startTime < Date.now();
}

export function eventHasConcluded(endTime) {
  return endTime < Date.now();
}

// startTime and endTime are Date objects
export function getRelativeStartTime(startTime, endTime) {
  const now = new Date();

  if (now < startTime) {
    // Return it in days, hours, minutes or seconds based on the difference between now and the start time
    const diff = startTime - now;

    if (diff > 86400000) {
      return `Starting in ${Math.floor(diff / 86400000)} days`;
    }

    if (diff > 3600000) {
      return `Starting in ${Math.floor(diff / 3600000)} hours`;
    }

    if (diff > 60000) {
      return `Starting in ${Math.floor(diff / 60000)} minutes`;
    }

    return `Starting in ${Math.floor(diff / 1000)} seconds`;
  }

  if (now > startTime && now < endTime) {
    return "LIVE";
  }

  if (now > endTime) {
    return "Concluded";
  }
}

export function getPresentersNames(presentation) {
  return presentation?.presenters?.map(presenter => presenter.name).join(", ");
}

// Formats a multi-day event date range with ordinal suffixes and time
// Handles different coincidence scenarios:
// - Same day: "January 19th, 2024, 3:00 PM - 5:00 PM"
// - Same month: "January 19th, 3:00 PM - 21st, 5:00 PM 2024"
// - Same year: "January 19th, 3:00 PM - February 2nd, 5:00 PM 2024"
// - Different years: "January 31st, 2024, 3:00 PM - February 2nd, 2025, 5:00 PM"
export function formatEventDateRange(data) {
  const startDate = new Date(data.start_time || data.start_time_next);
  const endDate = new Date(data.end_time || data.end_time_next);

  const startYear = startDate.getFullYear();
  const endYear = endDate.getFullYear();
  const startMonth = startDate.getMonth();
  const endMonth = endDate.getMonth();
  const startDay = startDate.getDate();
  const endDay = endDate.getDate();

  const getOrdinalSuffix = day => {
    if (day === 1 || day === 21 || day === 31) return "st";
    if (day === 2 || day === 22) return "nd";
    if (day === 3 || day === 23) return "rd";
    return "th";
  };

  const formatTime = date => {
    const options = {
      hour: "numeric",
      minute: "2-digit",
      hour12: true
    };
    return date.toLocaleTimeString("en-US", options);
  };

  const formatDayWithSuffix = day => `${day}${getOrdinalSuffix(day)}`;

  const formatMonthName = date => date.toLocaleDateString("en-US", { month: "long" });

  const startTime = formatTime(startDate);
  const endTime = formatTime(endDate);

  // Same day - show "January 19th, 2024, 3:00 PM - 5:00 PM"
  if (startYear === endYear && startMonth === endMonth && startDay === endDay) {
    const monthName = formatMonthName(startDate);
    const dayFormatted = formatDayWithSuffix(startDay);
    return `${monthName} ${dayFormatted}, ${startYear}, ${startTime} - ${endTime}`;
  }

  // Same month and year - show "January 19th, 3:00 PM - 21st, 5:00 PM 2024"
  if (startYear === endYear && startMonth === endMonth) {
    const monthName = formatMonthName(startDate);
    const startDayFormatted = formatDayWithSuffix(startDay);
    const endDayFormatted = formatDayWithSuffix(endDay);
    return `${monthName} ${startDayFormatted}, ${startTime} - ${endDayFormatted}, ${endTime} ${startYear}`;
  }

  // Same year - show "January 19th, 3:00 PM - February 2nd, 5:00 PM 2024"
  if (startYear === endYear) {
    const startMonthName = formatMonthName(startDate);
    const endMonthName = formatMonthName(endDate);
    const startDayFormatted = formatDayWithSuffix(startDay);
    const endDayFormatted = formatDayWithSuffix(endDay);
    return `${startMonthName} ${startDayFormatted}, ${startTime} - ${endMonthName} ${endDayFormatted}, ${endTime} ${startYear}`;
  }

  // Different years - show full dates "January 31st, 2024, 3:00 PM - February 2nd, 2025, 5:00 PM"
  const startMonthName = formatMonthName(startDate);
  const endMonthName = formatMonthName(endDate);
  const startDayFormatted = formatDayWithSuffix(startDay);
  const endDayFormatted = formatDayWithSuffix(endDay);
  return `${startMonthName} ${startDayFormatted}, ${startYear}, ${startTime} - ${endMonthName} ${endDayFormatted}, ${endYear}, ${endTime}`;
}
